/**
 * Cover sheet stored on the job, before a field report exists.
 *
 * Office staff enter this for a battery job that never goes through the field
 * app. A paired PV job can carry the same visit, so blank fields are copied
 * across and a value that is already present is left alone.
 */
import { z } from 'zod';
import { resolveInspectionOffsetMinutes } from './au-timezone.js';
import { RoofCavityNotInspectedReasonSchema } from './cer-portal.js';
import { estimateInspectionWindow, formatInstantForCover, parseTimestampToInstant, } from './inspection-window.js';
export const JOB_COVER_FIELDS = [
    'inspectionStartedAt',
    'inspectionEndedAt',
    'inspectorIdCardNumber',
    'roofCavityInspected',
    'roofCavityNotInspectedReasons',
    'roofCavityNotInspectedNote',
    'updatedOwnerEmail',
    'consentProviderIsHomeOwner',
    'consentProviderFirstName',
    'consentProviderSurname',
    'consentProviderEmail',
];
export const JOB_COVER_FIELD_LABELS = {
    inspectionStartedAt: 'Inspection start',
    inspectionEndedAt: 'Inspection end',
    inspectorIdCardNumber: 'Inspector ID card number',
    roofCavityInspected: 'Roof cavity inspected',
    roofCavityNotInspectedReasons: 'Roof cavity not inspected — reasons',
    roofCavityNotInspectedNote: 'Roof cavity not inspected — note',
    updatedOwnerEmail: 'Updated owner email',
    consentProviderIsHomeOwner: 'Consent provider is the owner',
    consentProviderFirstName: 'Consent provider first name',
    consentProviderSurname: 'Consent provider surname',
    consentProviderEmail: 'Consent provider email',
};
/** Fields the CER coversheet asks for on both a PV and a battery submission. */
export const SHARED_PORTAL_COVER_FIELDS = [
    'inspectionStartedAt',
    'inspectionEndedAt',
    'inspectorIdCardNumber',
    'roofCavityInspected',
    'updatedOwnerEmail',
];
export const JobCoverSchema = z.object({
    inspectionStartedAt: z.string().optional(),
    inspectionEndedAt: z.string().optional(),
    inspectorIdCardNumber: z.string().optional(),
    roofCavityInspected: z.boolean().optional(),
    roofCavityNotInspectedReasons: z.array(RoofCavityNotInspectedReasonSchema).optional(),
    roofCavityNotInspectedNote: z.string().optional(),
    updatedOwnerEmail: z.string().optional(),
    consentProviderIsHomeOwner: z.boolean().optional(),
    consentProviderFirstName: z.string().optional(),
    consentProviderSurname: z.string().optional(),
    consentProviderEmail: z.string().optional(),
});
const HAS_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/;
const STATES = new Set(['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT']);
export function isBlankCoverValue(value) {
    if (value == null)
        return true;
    if (typeof value === 'string')
        return value.trim() === '';
    if (Array.isArray(value))
        return value.length === 0;
    return false;
}
/** Copy only fields the target does not already have. */
export function fillCoverBlanks(target, source) {
    const cover = { ...(target ?? {}) };
    const filled = [];
    if (!source)
        return { cover, filled };
    for (const field of JOB_COVER_FIELDS) {
        if (!isBlankCoverValue(cover[field]) || isBlankCoverValue(source[field]))
            continue;
        assignCoverField(cover, field, source[field]);
        filled.push(field);
    }
    return { cover, filled };
}
function assignCoverField(cover, field, value) {
    if (isBlankCoverValue(value))
        return;
    switch (field) {
        case 'inspectionStartedAt':
        case 'inspectionEndedAt':
        case 'inspectorIdCardNumber':
        case 'roofCavityNotInspectedNote':
        case 'updatedOwnerEmail':
        case 'consentProviderFirstName':
        case 'consentProviderSurname':
        case 'consentProviderEmail':
            if (typeof value === 'string')
                cover[field] = value;
            return;
        case 'roofCavityInspected':
        case 'consentProviderIsHomeOwner':
            if (typeof value === 'boolean')
                cover[field] = value;
            return;
        case 'roofCavityNotInspectedReasons': {
            const parsed = z.array(RoofCavityNotInspectedReasonSchema).safeParse(value);
            if (parsed.success && parsed.data.length > 0)
                cover[field] = parsed.data;
            return;
        }
    }
}
function auState(value) {
    const upper = value?.trim().toUpperCase();
    return upper && STATES.has(upper) ? upper : undefined;
}
/** Naive local wall time. Zoned instants are shifted to the site. */
export function coverTimestampForSite(value, offsetMinutes) {
    const text = value?.trim();
    if (!text || !text.includes('T'))
        return undefined;
    if (!HAS_ZONE.test(text))
        return text;
    const instant = parseTimestampToInstant(text, offsetMinutes);
    return instant === undefined ? undefined : formatInstantForCover(instant, offsetMinutes);
}
function capturePhotos(photos) {
    return (photos ?? [])
        .filter((photo) => !photo.promptKey.includes('consent') && photo.captureTimeSource !== 'upload')
        .map((photo) => photo.capturedAt)
        .filter((value) => Boolean(value));
}
function photoWindow(photos, offsetMinutes) {
    const estimate = estimateInspectionWindow({
        capturedAts: capturePhotos(photos),
        deviceUtcOffsetMinutes: offsetMinutes,
    });
    if (!estimate?.sufficient)
        return {};
    return { startedAt: estimate.startedAt, endedAt: estimate.endedAt };
}
/**
 * Suggest values for blank cover fields.
 *
 * Times come from a field report's own stamps when one exists, then from the
 * paired job, then from photo capture times. Anything still blank stays blank
 * for the operator to type. A field that already has a value is never replaced.
 */
export function seedJobCover(input) {
    const offset = resolveInspectionOffsetMinutes({
        ...(auState(input.state) ? { state: auState(input.state) } : {}),
        ...(input.siteAddress ? { siteAddress: input.siteAddress } : {}),
        atMs: input.atMs,
    });
    const cover = { ...(input.cover ?? {}) };
    const seeds = [];
    const ownPhotos = photoWindow(input.photos, offset);
    const partnerPhotos = photoWindow(input.partner?.photos, offset);
    const at = (value) => coverTimestampForSite(value, offset);
    const take = (field, value, source) => {
        if (!isBlankCoverValue(cover[field]) || isBlankCoverValue(value))
            return;
        const stored = field === 'inspectionStartedAt' || field === 'inspectionEndedAt'
            ? at(typeof value === 'string' ? value : undefined)
            : value;
        if (isBlankCoverValue(stored))
            return;
        assignCoverField(cover, field, stored);
        if (!isBlankCoverValue(cover[field]))
            seeds.push({ field, source });
    };
    // Own report first, then the paired accreditation, then photos. A time the
    // report already recorded beats a photo estimate. A typed partner value
    // beats a photo estimate too.
    for (const field of JOB_COVER_FIELDS) {
        take(field, input.submission?.cover?.[field], 'submission-cover');
    }
    take('inspectionStartedAt', input.submission?.startedAt, 'submission-clock');
    take('inspectionEndedAt', input.submission?.submittedAt, 'submission-clock');
    for (const field of JOB_COVER_FIELDS) {
        take(field, input.partner?.cover?.[field], 'partner-cover');
        take(field, input.partner?.submission?.cover?.[field], 'partner-submission-cover');
    }
    take('inspectionStartedAt', input.partner?.submission?.startedAt, 'partner-submission-clock');
    take('inspectionEndedAt', input.partner?.submission?.submittedAt, 'partner-submission-clock');
    take('inspectionStartedAt', ownPhotos.startedAt, 'photo-exif');
    take('inspectionEndedAt', ownPhotos.endedAt, 'photo-exif');
    take('inspectionStartedAt', partnerPhotos.startedAt, 'partner-photo-exif');
    take('inspectionEndedAt', partnerPhotos.endedAt, 'partner-photo-exif');
    take('inspectorIdCardNumber', input.inspectorRegistrationNumber, 'inspector-profile');
    take('inspectorIdCardNumber', input.partner?.inspectorRegistrationNumber, 'partner-inspector-profile');
    take('updatedOwnerEmail', input.ownerEmail, 'owner-email');
    take('updatedOwnerEmail', input.partner?.ownerEmail, 'partner-owner-email');
    return { cover, seeds };
}
//# sourceMappingURL=job-cover.js.map